import { useContext } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { EFolderType, IFolder, TRootItems } from '../types/folders.types';
import { sortItems, sortSparseItems } from '../helpers/sortItems.helper';

import { AccessType } from '@modules/access/types';
import type {
  TItem,
  TItemType,
  TUniversalItemId,
  TVersionInfo,
  TCopyItem,
  TCopyVersionPreference,
  TItemNameUpdateResult,
  TItemRenameResult,
} from '../types/items.types';
import { TDocument, TDraft, TItemDependants, TUseDocumentResult, TVersion } from '@modules/documents';
import { TFile, TFileVersion, TUseFileResult } from '@modules/files';

import { ApiContext } from '@shared/contexts';
import { getQueryKey } from '@shared/helpers';
import { buildHref } from '@shared/utils';
import { SignInContext, TCustomFieldRaw } from '@modules/users';
import { TUseInlineBlockResult } from '@modules/inlineBlocks';
import { TBulkImport } from '../types/bulkImports.types';
import { usePollBulkImport } from './bulkImports.api';
import { TERMINOLOGY } from '@shared/i18n';
import {
  addItemsToFolderQueryData,
  updateCustomFieldValueQueryData,
  updateItemNameQueryData,
  updateReplicaNameQueryData,
} from './itemQueryData';
import { invalidateRegistrationQueries } from '@modules/registrations/helpers/registrationQueries';

// --- GET:

export const useVersionInfo = (id?: string, itemType?: TItemType) => {
  // empty returns null data
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const getVersionInfo = async (signal?: AbortSignal): Promise<TVersionInfo | null> => {
    if (!id || !itemType) return null;
    return apiClient.get(`/items/${id}/versions/${itemType}`, { signal }).then((res) => res.data);
  };

  const { data, isError, error } = useQuery({
    retry,
    queryKey: [`version-info-${id}`],
    queryFn: ({ signal }) => getVersionInfo(signal),
  });
  useHandleQueryError(isError, error, `Failed to load version info for item ${id}!`, 'Loading version details failed!');

  return { error, data };
};

export const useGetVersionInfo = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: { id: string; itemType: TItemType }): Promise<TVersionInfo> =>
    apiClient.get(`/items/${variables.id}/versions/${variables.itemType}`).then((res) => res.data);

  return useMutation({ mutationFn, onError: handleApiError(`Cannot access item!`, true) });
};

export const useGetDependants = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: { id: string; itemType: TItemType }): Promise<TItemDependants> =>
    apiClient.get(`/items/${variables.id}/dependants/${variables.itemType}`).then((res) => res.data);

  return useMutation({
    mutationFn,
    onError: handleApiError(`Failed to load item dependants!`, true),
  });
};

// --- POST:
export const useUpload = () => {
  const { apiClient, queryClient, handleApiError } = useContext(ApiContext);
  const { isPolling, setJobId, startProgress } = usePollBulkImport();

  const mutationFn = async (variables: {
    folder: IFolder;
    formData: FormData;
    documentImportConfigId?: string;
    isStaticFiles?: boolean;
  }): Promise<TBulkImport> => {
    const cancelProgress = startProgress();
    try {
      const importHref = buildHref(`/items/${variables.folder._id}/import`, {
        'document-import-config': variables.isStaticFiles ? undefined : variables.documentImportConfigId,
        'static-files': variables.isStaticFiles ? 'true' : undefined,
      });
      const res = await apiClient.post<TBulkImport>(importHref, variables.formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    } catch (e) {
      cancelProgress();
      throw e;
    }
  };

  const mutation = useMutation({
    mutationFn,
    onError: handleApiError('Importing files failed!', true),
    onSuccess: (result, variables) => {
      setJobId(result._id);
      queryClient.setQueryData([`bulk-import-${result._id}`], () => result);
      queryClient.setQueryData([`folder-${variables.folder._id}`], (oldData: IFolder | undefined) => ({
        ...oldData,
        isProcessing: true,
      }));
    },
  });

  return { ...mutation, isPending: mutation.isPending || isPolling };
};

// --- PUT:

export const useRename = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    parent?: IFolder; // specify to update use-folder query
    isProduct?: boolean;
    id: string;
    itemType: TItemType;
    name: string;
  }): Promise<TItemRenameResult | undefined> =>
    await apiClient
      .put(`/items/${variables.id}/rename/${variables.itemType}`, { name: variables.name })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      show('success', 'Item renamed.');
      const nameUpdate = {
        name: data?.name ?? variables.name,
        pdfExportFileNames: data?.pdfExportFileNames,
      };
      if (['DOCUMENT', 'FILE'].includes(variables.itemType))
        updateItemNameQueryData(queryClient, variables, nameUpdate);
      if (['REPLICA_DOCUMENT', 'REPLICA_FILE'].includes(variables.itemType))
        updateReplicaNameQueryData(queryClient, variables.id, nameUpdate);
      if (variables.isProduct || variables.parent)
        return queryClient.setQueryData(
          [variables.parent ? `folder-${variables.parent._id}` : 'products'],
          (oldData: IFolder | TRootItems | undefined): IFolder | TRootItems | undefined => {
            if (!oldData) return oldData;
            let jurisdictions = (oldData as IFolder).jurisdictions;
            if (variables.parent?.resolvedPath.length === 0)
              jurisdictions = sortSparseItems(
                jurisdictions.map((item) => (item._id === variables.id ? { ...item, name: nameUpdate.name } : item))
              );
            return {
              ...oldData,
              jurisdictions,
              children: sortItems(
                oldData.children.map((item) => (item._id === variables.id ? { ...item, name: nameUpdate.name } : item))
              ),
            };
          }
        );
      if (variables.itemType === 'FOLDER')
        return queryClient.setQueryData(
          [`folder-${variables.id}`],
          (oldData: IFolder | undefined): IFolder | undefined => {
            if (!oldData) return oldData;
            return { ...oldData, name: nameUpdate.name };
          }
        );
      if (variables.itemType === 'INLINE_BLOCK')
        return queryClient.setQueryData(
          [`inline-block-${variables.id}`],
          (oldData: TUseInlineBlockResult | undefined): TUseInlineBlockResult | undefined => {
            if (!oldData) return oldData;
            return { ...oldData, inlineBlock: { ...oldData.inlineBlock, name: nameUpdate.name } };
          }
        );
    },
    onError: handleApiError('Renaming item failed!'),
  });
};

export const useMove = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    parent?: IFolder; // specify to update use-folder query
    items: TUniversalItemId[];
    targetId: string;
  }): Promise<void> =>
    await apiClient.put(`/items/${variables.targetId}/move`, { items: variables.items }).then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      show('success', `Item${variables.items.length === 1 ? '' : 's'} moved.`);
      if (!variables.parent) return;

      const movedIds = new Set(variables.items.map((i) => i._id));
      queryClient.setQueryData([`folder-${variables.parent._id}`], (oldData: IFolder | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          children: oldData.children.filter((item) => !movedIds.has(item._id)),
        };
      });
      queryClient.invalidateQueries({ queryKey: [`folder-${variables.targetId}`] }); // staleTime is 5min - mark target stale so next visit refetches
    },
    onError: handleApiError('Moving items failed!'),
  });
};

export const useCopy = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    destinationFolderId: string;
    items: TCopyItem[];
    versionPreference?: TCopyVersionPreference;
    versionId?: string;
    pull?: boolean; // we call it pull in the variables as we also this property for post-processing
    successMessage?: string | false; // custom toast message, or false to suppress
  }): Promise<TItem[]> =>
    await apiClient
      .put(`/items/${variables.destinationFolderId}/copy`, {
        items: variables.items,
        copyMaster: variables.pull,
        versionPreference: variables.versionPreference,
        versionId: variables.versionId,
      })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (newItems: TItem[], variables) => {
      if (variables.successMessage !== false) {
        const msg =
          variables.successMessage ||
          `Item${variables.items.length === 1 ? '' : 's'} ${variables.pull ? 'pulled' : 'copied'}.`;
        show('success', msg);
      }

      if (!variables.destinationFolderId) return;
      addItemsToFolderQueryData(queryClient, variables.destinationFolderId, newItems);
    },
    onError: handleApiError(), // default message
  });
};

export const useCloneStructure = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: { targetFolderId: string; sourceFolderId: string }): Promise<TItem[]> =>
    await apiClient
      .put(`/items/${variables.targetFolderId}/clone-structure`, { sourceFolder: variables.sourceFolderId })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (newItems: TItem[], variables) => {
      show('success', 'Structure cloned.');
      addItemsToFolderQueryData(queryClient, variables.targetFolderId, newItems);
    },
    onError: handleApiError('Cloning structure failed!', true),
  });
};

/*
export const useCopy = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    parentId?: string; // specify to update use-folder query
    sourceId: string;
    itemType: TItemType;
  }): Promise<TItem> =>
    await apiClient.put(`/items/${variables.sourceId}/copy/${variables.itemType}`).then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (newItem: TItem, variables) => {
      show('success', 'Item copied.');
      if (!variables.parentId) return;

      queryClient.setQueryData([`folder-${variables.parentId}`], (oldData: TFolder | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          children: sortItems([...oldData.children, newItem]),
        };
      });
    },
    onError: handleApiError('Copying item failed!'),
  });
};

export const usePull = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: { parentId: string; items: { _id: string; itemType: TItemType }[] }): Promise<TItem[]> => {
    const { data } = await apiClient.post<TItem[]>(`/items/pull/${variables.parentId}`, { items: variables.items });
    return data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (pulledItems, variables) => {
      show('success', 'Items pulled.');

      queryClient.setQueryData([`folder-${variables.parentId}`], (oldData: TFolder | undefined) =>
        oldData
          ? {
              ...oldData,
              children: sortItems([...oldData.children, ...pulledItems]),
            }
          : oldData
      );
    },
    onError: handleApiError('Pulling items failed!'),
  });
};

*/

// replicas are not supported yet:
export const useUpdateVersion = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async ({
    id,
    itemType,
    versionId,
  }: {
    id: string;
    itemType: TItemType;
    versionId: string;
  }): Promise<boolean> =>
    await apiClient.put(`/items/${id}/version/${itemType}?version=${versionId}`).then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (updatedVersion, variables) => {
      show('success', `Version was updated to v${variables.versionId}.`);
      if (updatedVersion) {
        queryClient.invalidateQueries({ queryKey: [getQueryKey(variables.itemType, variables.id)] });
        queryClient.invalidateQueries({ queryKey: [`version-info-${variables.id}`] });
      }
    },
    onError: handleApiError(), // default message
  });
};

export type TGenerateUniqueIdResult = TItemNameUpdateResult & { value: TCustomFieldRaw };

export const useUpsertCustomFieldValue = () => {
  const { apiClient, queryClient, handleApiError } = useContext(ApiContext);
  const { user } = useContext(SignInContext);

  const mutationFn = async (variables: {
    id: string;
    itemType: TItemType;
    fieldId: string;
    value: TCustomFieldRaw;
    parentId?: string;
  }): Promise<TItemNameUpdateResult> =>
    apiClient
      .put(`/items/${variables.id}/custom-field-values/${variables.itemType}/${variables.fieldId}`, {
        value: variables.value,
      })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      updateCustomFieldValueQueryData(queryClient, variables, user._id);
      if (data?.name) updateItemNameQueryData(queryClient, variables, { ...data, name: data.name });
    },
    onError: handleApiError('Saving custom field value failed!', true),
  });
};

export const useGenerateCustomFieldUniqueId = () => {
  const { apiClient, queryClient, handleApiError } = useContext(ApiContext);
  const { user } = useContext(SignInContext);

  const mutationFn = async (variables: {
    id: string;
    itemType: TItemType;
    fieldId: string;
    parentId?: string;
  }): Promise<TGenerateUniqueIdResult> => {
    return apiClient
      .post(`/items/${variables.id}/custom-field-values/${variables.itemType}/${variables.fieldId}/generate`)
      .then((res) => res.data);
  };

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      updateCustomFieldValueQueryData(queryClient, { ...variables, value: data.value }, user._id);
      if (data?.name) updateItemNameQueryData(queryClient, variables, { ...data, name: data.name });
    },
    onError: handleApiError('Generating custom field value failed!', true),
  });
};

export const useUpdateFrozen = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: { id: string; folderType: EFolderType; isFrozen: boolean }): Promise<void> => {
    await apiClient.put(`/items/${variables.id}/frozen`, { isFrozen: variables.isFrozen });
  };

  return useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      const itemTypeName =
        variables.folderType === EFolderType.COUNTRY
          ? TERMINOLOGY.registrationSingle.upper
          : variables.folderType === EFolderType.SNAPSHOT
            ? TERMINOLOGY.snapshot.upper
            : 'Folder';
      show(
        'success',
        `${itemTypeName} has been ${variables.isFrozen ? TERMINOLOGY.frozen.lower : TERMINOLOGY.unfrozen.lower}.`
      );
      if (variables.folderType === EFolderType.COUNTRY) invalidateRegistrationQueries(queryClient);

      // Update the folder query cache to set isFrozen for the folder and all its children
      queryClient.setQueryData([`folder-${variables.id}`], (oldData: IFolder | undefined): IFolder | undefined => {
        if (!oldData) return oldData;

        // Helper function to update accessRight based on isFrozen
        const getUpdatedAccessRight = (item: TItem | IFolder): AccessType =>
          variables.isFrozen ? Math.min(item.accessRight, AccessType.READ) : item.accessRightAllowFrozen;

        // Helper function to recursively update isFrozen for all children
        const updateIsFrozen = (items: TItem[]): TItem[] =>
          items.map((item) => ({
            ...item,
            isFrozen: variables.isFrozen,
            accessRight: getUpdatedAccessRight(item),
          }));

        return {
          ...oldData,
          isFrozen: variables.isFrozen,
          accessRight: getUpdatedAccessRight(oldData),
          children: updateIsFrozen(oldData.children),
        };
      });
    },
    onError: handleApiError('Updating frozen status failed!'),
  });
};

// --- DELETE:

export const useArchive = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    isProduct?: boolean;
    parent?: IFolder; // specify to update use-folder query
    sourceId: string;
    itemType: TItemType;
  }): Promise<void> =>
    await apiClient.delete(`/items/${variables.sourceId}/archive/${variables.itemType}`).then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      show('success', 'Item archived.');

      if (variables.parent || variables.isProduct)
        queryClient.setQueryData(
          [variables.parent ? `folder-${variables.parent._id}` : 'products'],
          (oldData: IFolder | TRootItems | undefined) => {
            if (!oldData) return oldData;
            let jurisdictions = (oldData as IFolder).jurisdictions;
            if (variables.parent?.resolvedPath.length === 0)
              jurisdictions = jurisdictions.filter((item) => item._id !== variables.sourceId);

            return {
              ...oldData,
              jurisdictions,
              children: oldData.children.filter((item) => item._id !== variables.sourceId),
            };
          }
        );
    },
    onError: handleApiError('Archiving item failed!'),
  });
};
